import { Button, Col, Collapse, Form, Input, Modal, Popconfirm, Row, Select, Spin, Typography } from 'antd';
import {
  ArrowDownIcon,
  CheckIcon,
  CrossIcon,
  DashIcon,
  ExpandMinimizeModalIcon,
  PlusIcon,
  RefreshIcon,
  SearchIcon,
  TrashBinIcon,
  TrashBinWhiteIcon
} from 'assets';
import CreateTicketForm, { CreateTicketFormRefProps } from 'components/create-ticket/CreateTicketForm';
import CreateTicketInStructions from 'components/create-ticket/CreateTicketInStructions';
import { ServiceTypeList, ServiceTypeListOptions } from 'components/service-types';
import { useDistrictsPaging, useWardsByDistrictId } from 'hooks/administrative-units';
import { useCustomerNameByPhoneNumber } from 'hooks/tickets/useGetCustomerNameByPhoneNumber';
import { customerLookupDetailedMessages } from 'messages/customer-lookup-detailed.message';
import { customerLookupMessages } from 'messages/customer-lookup.message';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetServiceTypeDetailQuery, useLazyGetServiceTypeDetailQuery } from 'services';
import { RootState } from 'store';
import { useMinimizedModals } from 'pages/CustomerSupport/hooks/useMinimizedModals';
import dayjs from 'dayjs';
import {
  notifyLookupFormUpdated,
  safeUpdateSearchCriteria,
  updateMinimizedTicketFields
} from 'pages/CustomerSupport/utils/formSyncHelper';
import VipCustomerLookup from './VipCustomerLookup';

const { Panel } = Collapse;

// Define CustomerLookupFormValue interface if it's not already defined elsewhere
interface CustomerLookupFormValue {
  customerName?: string;
  customerAddress?: string;
  districtId?: number;
  wardId?: number | { value: number; label: string };
  customerPhoneNumber?: string;
  customerRequestCode?: string;
  customerCode?: string;
  meterSerialNumber?: string;
  routeCode?: string;
  meterPointCode?: string;
}

// Define the modal interface
interface MinimizedModal {
  id: number;
  name: string;
  formData: any;
}

interface CustomerLookupFormProps {
  onSearch: (criteria: any) => void;
  initialValues?: any;
  isChatSelected?: boolean; // Add new prop to track chat selection state
  isInDetailView?: boolean; // This prop might be re-evaluated or removed if displayGlobalModals covers its intent for modals
  displayGlobalModals?: boolean; // New prop to control modal display
  currentSessionId?: string | number; // ✅ ADD: New prop for session ID
  tabKey?: string; // ✅ ADD: New prop for tab key to filter minimized modals
}

const CustomerLookupForm: React.FC<CustomerLookupFormProps> = ({
  onSearch,
  initialValues,
  isChatSelected = false, // Default to false if not provided
  isInDetailView = false, // Existing prop
  displayGlobalModals = true, // Default to true for standalone usage (e.g., in CustomerLookupView)
  currentSessionId, // ✅ ADD: Extract currentSessionId prop
  tabKey // ✅ ADD: Extract tabKey prop
}) => {
  const [form] = Form.useForm<CustomerLookupFormValue>();
  const dispatch = useDispatch();
  // ✅ FIX: Move the declaration of disabledFields before it's used in the useEffect
  const [disabledFields, setDisabledFields] = useState<Record<string, 'default' | 'enabled' | 'disabled'>>({});
  const [districtId, setDistrictId] = useState(0);
  const [showQuickSelect, setShowQuickSelect] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [isServiceTypeModalOpen, setIsServiceTypeModalOpen] = useState(false);
  const [isCreateTicketModalOpen, setIsCreateTicketModalOpen] = useState(false);
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState<number | null>(null);
  const [serviceTypeId, setServiceTypeId] = useState<number | undefined>(undefined);
  // const [tabs, setTabs] = useState<TabData[]>([]); // Removed as it's not used here
  // const [tabKey] = useState<string>('customerLookup'); // Removed as it's not used here
  const formRef = useRef<CreateTicketFormRefProps>(null);
  const [searchBy, setSearchBy] = useState<string>('customerCode');
  const { data: serviceType, isLoading: isLoadingServiceTypeDetail } = useGetServiceTypeDetailQuery(
    selectedServiceTypeId!,
    {
      skip: !serviceTypeId,
      refetchOnMountOrArgChange: true
    }
  );

  const { districts, isLoading: loadingDistricts } = useDistrictsPaging({});
  const { wards, isLoading: loadingWards } = useWardsByDistrictId(90000);

  const [customerNumber, setCustomerNumber] = useState('');

  // 🚀 READ PENDING PHONE WHEN FORM MOUNT
  useEffect(() => {
    try {
      const pendingPhone = localStorage.getItem('pendingLookupPhone');
      if (pendingPhone) {
        // Map vào form và state
        form.setFieldsValue({ customerPhoneNumber: pendingPhone });
        setCustomerNumber(pendingPhone);
        // Enable field để user thấy số ngay
        setDisabledFields((prev) => ({ ...prev, customerPhoneNumber: 'enabled' }));
        // Trigger search tự động
        onSearch({ customerPhoneNumber: pendingPhone });
        // Xoá buffer
        localStorage.removeItem('pendingLookupPhone');
      }
    } catch (e) {
      console.warn('Cannot read pendingLookupPhone', e);
    }
  }, []);
  // ✅ ADD: isLoading state from the hook
  const { customerNameByPhoneNumber, isLoading: isLoadingCustomerName } = useCustomerNameByPhoneNumber(customerNumber);
  const [customerPhoneLabel, setCustomerPhoneLabel] = useState(customerLookupMessages.customerPhone);
  const [isServiceTypeOptionsModalOpen, setIsServiceTypeOptionsModalOpen] = useState(false);
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null);
  const [isRotating, setIsRotating] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [customerCodeLength, setCustomerCodeLength] = useState(0);
  const [isCallTriggered, setIsCallTriggered] = useState(false);
  // const [visibleModalId, setVisibleModalId] = useState<number | undefined>(); // Removed as it's not used here
  const isCallSelected = useSelector((state: RootState) => state.customerSupportSession.isCallSelected);
  // const [serviceTypeNames, setServiceTypeNames] = useState<Record<number, string>>({}); // Removed as it's not used here

  // ✨ ADD: Missing state variables for loading and feedback
  const [isSearching, setIsSearching] = useState(false);

  // ✅ ADD: State and refs for improved customer name display logic
  const [displayCustomerName, setDisplayCustomerName] = useState<string | null>(null);
  const customerNameTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevCustomerNumberRef = useRef<string | undefined>();
  const processingNameForNumberRef = useRef<string | undefined>();

  // ✅ ADD: Key for forcing CreateTicketForm remount
  const [createTicketModalKey, setCreateTicketModalKey] = useState(0);
  const [modalOpenTimestamp, setModalOpenTimestamp] = useState(0); // Track when modal was opened

  // 🚨 ADD: Listen for formFullReset event to clear form after ticket completion
  useEffect(() => {
    const handleFormFullReset = (event: CustomEvent) => {
      // Reset form fields
      form.resetFields();

      // Reset all component state
      setCustomerNumber('');
      setDisplayCustomerName(null);
      setSearchKeyword('');
      setSelectedServiceTypeId(null);
      setServiceTypeId(undefined);
      setCustomerCodeLength(0);
      setIsCallTriggered(false);
      setSearchBy('customerCode');

      // Reset disabled fields state
      setDisabledFields({});

      // Reset customer phone label
      setCustomerPhoneLabel(customerLookupMessages.customerPhone);
    };

    window.addEventListener('formFullReset', handleFormFullReset as EventListener);

    return () => {
      window.removeEventListener('formFullReset', handleFormFullReset as EventListener);
    };
  }, [form]);

  // Use the minimized modals hook
  const {
    minimizedModals: allMinimizedModals,
    setMinimizedModals,
    handleMinimizeModal,
    handleDeleteMinimizedModal,
    handleRestoreModal,
    initializeMinimizedModals,
    syncWithSessionStorage
  } = useMinimizedModals(
    currentSessionId,
    undefined, // No saveSessionTabs for this component
    isCallSelected,
    isChatSelected
  );

  // Filter minimized modals by current tab key
  const minimizedModals = useMemo(() => {
    // 🚨 SESSION ISOLATION: Only show modals from current session
    if (!currentSessionId) {
      return [];
    }

    // If tabKey is provided, filter by specific tab
    // If tabKey is undefined (global lookup view), show all modals from current session only

    try {
      const sessionData = JSON.parse(localStorage.getItem('customerSupportSession') || '[]');

      const currentSession = sessionData.find(
        (s: any) =>
          (typeof currentSessionId === 'string' && s.callId === currentSessionId) ||
          (typeof currentSessionId === 'number' && s.chatSessionId === currentSessionId)
      );

      if (!currentSession?.searchData) {
        return [];
      }

      // 🚨 CASE 1: Specific tab (UC tabs) - show only that tab's modals
      if (tabKey) {
        const currentTab = currentSession.searchData.find((t: any) => t.key === tabKey);

        if (!currentTab?.minimizedModals) {
          return [];
        }

        const filteredModals = allMinimizedModals.filter((modal) => currentTab.minimizedModals.includes(modal.id));

        return filteredModals;
      }

      // 🚨 CASE 2: Global lookup (no tabKey) - show all modals from current session only
      const allSessionModalIds = new Set<number>();
      for (const tab of currentSession.searchData) {
        if (tab.minimizedModals && tab.formData) {
          for (const modalId of tab.minimizedModals) {
            if (tab.formData[modalId]) {
              allSessionModalIds.add(modalId);
            }
          }
        }
      }

      const sessionFilteredModals = allMinimizedModals.filter((modal) => allSessionModalIds.has(modal.id));

      return sessionFilteredModals;
    } catch (error) {
      return allMinimizedModals;
    }
  }, [allMinimizedModals, tabKey, currentSessionId]);

  // ✅ ADD: New useEffect to handle changes in initialValues prop
  useEffect(() => {
    // This effect is crucial for when initialValues prop changes,
    // e.g., when navigating back from a detail view to the lookup view,
    // or when the form needs to be initialized with specific criteria from a search.
    if (initialValues) {
      const formValuesToSet = { ...initialValues };
      if (
        initialValues.wardId &&
        typeof initialValues.wardId === 'object' &&
        initialValues.wardId.hasOwnProperty('value')
      ) {
        formValuesToSet.wardId = initialValues.wardId.value;
      }
      // If initialValues explicitly sets wardId to null or undefined, ensure it's passed as undefined
      // to form.setFieldsValue to clear the field.
      // Example: if initialValues = { districtId: 123, wardId: null }
      // formValuesToSet will be { districtId: 123, wardId: null }
      // form.setFieldsValue will handle this. If wardId is not in initialValues, it won't be in formValuesToSet,
      // and setFieldsValue won't touch the existing wardId in the form.
      // However, to ensure clearing, if initialValues has wardId: null/undefined, it should be set.
      if (
        initialValues.hasOwnProperty('wardId') &&
        (initialValues.wardId === null || initialValues.wardId === undefined)
      ) {
        formValuesToSet.wardId = undefined;
      }

      // console.log('🔍 CustomerLookupForm DEBUG - Setting form values:', {
      //   originalInitialValues: initialValues,
      //   formValuesToSet: formValuesToSet,
      //   currentFormValues: form.getFieldsValue()
      // });

      // form.setFieldsValue(formValuesToSet); // Apply all initialValues to the form. This is the source of truth for display.

      // console.log('🔍 CustomerLookupForm DEBUG - After setting form values:', {
      //   newFormValues: form.getFieldsValue()
      // });

      // After the form's values are set from initialValues,
      // synchronize the local 'districtId' state (used for fetching wards)
      // with the actual value now in the form for 'districtId'.
      const currentFormDistrictId = form.getFieldValue('districtId');
      if (typeof currentFormDistrictId === 'number') {
        // Only update state if it's different to prevent unnecessary re-renders/fetches
        if (districtId !== currentFormDistrictId) {
          setDistrictId(currentFormDistrictId);
        }
      } else if (currentFormDistrictId === undefined || currentFormDistrictId === null) {
        // If form's districtId is cleared (e.g., by initialValues not containing it or setting it to null/undefined),
        // reset local state for ward fetching to 0.
        if (districtId !== 0) {
          // Only update state if it's different
          setDistrictId(0);
        }
      }
    } else {
      // This case handles when initialValues itself becomes null/undefined (e.g. parent wants a full reset).
      form.resetFields(); // Reset all form fields to their defaults or undefined.
      if (districtId !== 0) {
        // Only update state if it's different
        setDistrictId(0); // Reset local districtId state.
      }
    }
  }, [initialValues, form]); // Dependencies: initialValues, form.
  // The local 'districtId' state is now primarily a derivative of the form's state,
  // which is driven by 'initialValues' or direct user input via Select onChange.
  // Removing 'districtId' from this effect's dependencies prevents it from re-triggering
  // when 'districtId' state changes due to user interaction (which could cause unwanted reversions
  // if initialValues hadn't caught up).

  // Add listener for the custom incoming call event (This one is kept)
  useEffect(() => {
    const handleIncomingCall = (event: CustomEvent) => {
      const { customerNumber, forceReset } = event.detail;
      // Only do complete reset if forceReset flag is true
      if (forceReset) {
        // Reset form and state
        form.resetFields();
        setDisabledFields({});
        setCustomerCodeLength(0);
        setSearchBy('customerCode');
        setDisplayCustomerName(null);
        setCustomerPhoneLabel(customerLookupMessages.customerPhone);
      }

      if (customerNumber) {
        setTimeout(() => {
          setCustomerNumber(customerNumber);
          setIsCallTriggered(true);

          // Only set the phone number field
          form.setFieldsValue({
            customerPhoneNumber: customerNumber
          });

          // Enable just the phone number field
          setDisabledFields((prev) => ({
            ...prev,
            customerPhoneNumber: 'enabled'
          }));
        }, 50);
      }
    };

    window.addEventListener('incomingCallReceived', handleIncomingCall as EventListener);

    return () => {
      window.removeEventListener('incomingCallReceived', handleIncomingCall as EventListener);
    };
  }, [form, customerLookupMessages.customerPhone]);

  // ✅ FIXED: Separate useEffect for immediate onFinish trigger with safeguards against carrying over old data
  useEffect(() => {
    if (isCallTriggered && customerNumber) {
      // Create a clean search object with JUST the phone number
      const cleanSearchCriteria = {
        customerPhoneNumber: customerNumber
      };

      // ✅ FIX: Add small delay to ensure state has propagated
      setTimeout(() => {
        // Try to get the callId from localStorage if currentSessionId is still undefined
        if (!currentSessionId) {
          try {
            const callData = localStorage.getItem('currentCallDialingEvent');
            if (callData) {
              const parsedData = JSON.parse(callData);
              const callId = parsedData.parentCallUuid || parsedData.callUuid;
            }
          } catch (error) {
            console.error('Error retrieving call data during search:', error);
          }
        }

        // Search with only the phone number from the call
        onFinish(cleanSearchCriteria);
        setIsCallTriggered(false);
      }, 150);
    }
  }, [isCallTriggered, customerNumber, currentSessionId]);
  function removeVietnameseTones(str: string): string {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // remove diacritics
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D');
  }

  // REMOVE THIS USEEFFECT BLOCK - It will be replaced by a more comprehensive one below
  // useEffect(() => {
  //   if (customerNumber && (customerNameByPhoneNumber?.length ?? 0) > 0) {
  //     setCustomerPhoneLabel(`${customerLookupMessages.customerPhone}`);
  //   } else if (customerNumber) {
  //     setCustomerPhoneLabel(`${customerLookupMessages.customerPhone}`);
  //   } else {
  //     setCustomerPhoneLabel(customerLookupMessages.customerPhone);
  //   }
  // }, [customerNumber, customerNameByPhoneNumber]);

  // ✅ REVISED: Customer name and label processing logic
  useEffect(() => {
    if (customerNameTimeoutRef.current) {
      clearTimeout(customerNameTimeoutRef.current);
      customerNameTimeoutRef.current = null;
    }

    if (customerNumber) {
      // If the actual customerNumber has changed to a new value, or if we were not processing any number before
      if (prevCustomerNumberRef.current !== customerNumber || !processingNameForNumberRef.current) {
        setDisplayCustomerName(null); // Clear name from the old number or if it was null
        // Set label to "Đang xác định..." immediately if loading or no name yet
        setCustomerPhoneLabel(`${customerLookupMessages.customerPhone} (Đang xác định...)`);
        processingNameForNumberRef.current = customerNumber; // Start processing this new number
      }

      if (isLoadingCustomerName) {
        // If currently loading, ensure "Đang xác định..." is shown if no name is displayed yet
        if (!displayCustomerName) {
          setCustomerPhoneLabel(`${customerLookupMessages.customerPhone} (Đang xác định...)`);
        }
      } else if (customerNameByPhoneNumber && customerNameByPhoneNumber.length > 0) {
        const name = String(customerNameByPhoneNumber[0]);
        setDisplayCustomerName(name);
        setCustomerPhoneLabel(`${name} - ${customerLookupMessages.customerPhone}`);
        processingNameForNumberRef.current = customerNumber; // Confirm processing for this number succeeded
      } else {
        // Not loading and no name found
        // If we were processing this number and it yielded no name (and not loading anymore)
        if (processingNameForNumberRef.current === customerNumber) {
          // Set a timeout to revert label and clear display name if no name is found after a while
          customerNameTimeoutRef.current = setTimeout(() => {
            // Check if we are still on the same number and still no name after timeout
            if (
              processingNameForNumberRef.current === customerNumber &&
              (!customerNameByPhoneNumber || customerNameByPhoneNumber.length === 0) &&
              !isLoadingCustomerName
            ) {
              setCustomerPhoneLabel(customerLookupMessages.customerPhone);
              setDisplayCustomerName(null);
            }
          }, 2000); // 2-second timeout
        }
        // If no name is currently displayed, ensure label reflects "Đang xác định..." or default
        // This handles the case where loading finishes quickly with no result.
        if (!displayCustomerName) {
          setCustomerPhoneLabel(customerLookupMessages.customerPhone); // Or "Không tìm thấy" if preferred
        }
      }
    } else {
      // No customerNumber
      setDisplayCustomerName(null);
      setCustomerPhoneLabel(customerLookupMessages.customerPhone);
      processingNameForNumberRef.current = undefined;
    }

    prevCustomerNumberRef.current = customerNumber;
  }, [customerNumber, customerNameByPhoneNumber, isLoadingCustomerName, customerLookupMessages.customerPhone]);

  const handleSelectWard = (value: number) => {
    setDistrictId(value);
    form.setFieldsValue({ wardId: undefined });
  };

  const cycleFieldState = (fieldName: string) => {
    setDisabledFields((prev) => {
      const newValue: 'default' | 'enabled' | 'disabled' =
        prev[fieldName] === 'default' ? 'enabled' : prev[fieldName] === 'enabled' ? 'disabled' : 'default';

      const newState: Record<string, 'default' | 'enabled' | 'disabled'> = {
        ...prev,
        [fieldName]: newValue
      };

      // When customerDistrictAndWard is disabled, values are retained in the form.
      // The onFinish function will handle not searching by them.
      // No need to clear form.setFieldsValue here for districtId/wardId.

      return newState;
    });
  };

  // Render label function
  const renderLabel = (fieldName: string, label: string) => {
    const state = disabledFields[fieldName];
    return (
      <div className='flex cursor-pointer items-center justify-between' onClick={() => cycleFieldState(fieldName)}>
        <Typography.Text
          className={
            state === 'default'
              ? 'text-xs font-semibold text-black'
              : state === 'enabled'
                ? 'text-xs font-semibold text-green-600'
                : 'text-xs font-semibold text-gray-400'
          }
        >
          {label}
        </Typography.Text>
        {state === 'enabled' && <CheckIcon className='ml-1 text-sm' />}
        {state === 'disabled' && <CrossIcon className='ml-1 text-sm' />}
      </div>
    );
  };

  // ✅ ADD: New function to sync lookup form changes to minimized ticket forms
  const syncFieldToMinimizedModals = useCallback(
    (fieldName: string, value: any) => {
      updateMinimizedTicketFields(currentSessionId, fieldName, value, wards);
    },
    [currentSessionId, wards]
  );

  // ✅ ADD: Function to directly sync form values to open ticket forms
  const syncFormValuesToOpenTicket = useCallback(() => {
    if (!currentSessionId) return;

    try {
      // Get all current form values
      const allValues = form.getFieldsValue();

      // Notify all ticket forms about these values
      notifyLookupFormUpdated(currentSessionId, allValues);

      // Also sync to minimized modals
      Object.entries(allValues).forEach(([key, value]) => {
        if (value) {
          updateMinimizedTicketFields(currentSessionId, key, value, wards);
        }
      });
    } catch (error) {
      console.error('❌ Error syncing form values:', error);
    }
  }, [currentSessionId, form, wards]);

  // ✅ FIXED: Only sync values when user actually changes form, not on mount with initialValues
  // This prevents overwriting existing formData when restoring form state
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  useEffect(() => {
    // Only sync to minimized modals if user has actually interacted with the form
    // This prevents overwriting existing formData when component mounts with initialValues
    if (currentSessionId && hasUserInteracted) {
      setTimeout(() => {
        syncFormValuesToOpenTicket();
      }, 150);
    }
  }, [currentSessionId, syncFormValuesToOpenTicket, hasUserInteracted]);

  // ✅ UPDATE: Enhanced field change handler to sync to minimized modals
  const handleFieldChange = (fieldName: string, value: any) => {
    // Mark that user has interacted with the form
    setHasUserInteracted(true);

    setDisabledFields((prev) => ({
      ...prev,
      [fieldName]: value ? 'enabled' : 'default'
    }));

    // ✅ FIX: Only sync changes to minimized modals if currentSessionId exists
    if (value && currentSessionId) {
      syncFieldToMinimizedModals(fieldName, value);
    }
  };

  // ✅ ENHANCED: Better handling of onFinish to sync values
  const onFinish = async (values: any) => {
    // Mark that user has interacted with the form
    setHasUserInteracted(true);

    setIsSearching(true);

    try {
      // Xử lý khoảng trắng cho customerCode trước khi tìm kiếm
      if (searchBy === 'customerCode' && values.customerCode) {
        if (values.customerCode.length > 13) return;

        // Đảm bảo xóa hết khoảng trắng trước khi tìm kiếm
        const cleanedCode = values.customerCode.replace(/\s+/g, '');
        values.customerCode = cleanedCode;
        form.setFieldValue('customerCode', cleanedCode);
        setCustomerCodeLength(cleanedCode.length);
      }

      // Determine if the district/ward group is disabled
      const isDistrictWardGroupDisabled = disabledFields.customerDistrictAndWard === 'disabled';

      const filteredValues = Object.entries(values)
        .filter(([key, value]) => {
          // 1. Always exclude empty/null/undefined values
          if (value === undefined || value === null || value === '') {
            return false;
          }

          // 2. Luôn loại bỏ districtId khỏi điều kiện tìm kiếm vì đã cố định là 90000
          if (key === 'districtId') {
            return false;
          }

          // 3. If the district/ward group is disabled, specifically exclude wardId
          //    from the search criteria, even if they have values in the form.
          if (isDistrictWardGroupDisabled && key === 'wardId') {
            return false;
          }

          // 4. Exclude any other individually disabled fields
          if (disabledFields[key] === 'disabled') {
            return false;
          }

          return true; // Include the field if none of the above conditions were met
        })
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      // ✅ FIX: Check if we have a valid call session, if not, try to get it
      let sessionIdToUse = currentSessionId;
      if (!sessionIdToUse) {
        try {
          const callData = localStorage.getItem('currentCallDialingEvent');
          if (callData) {
            const parsedData = JSON.parse(callData);
            sessionIdToUse = parsedData.parentCallUuid || parsedData.callUuid;
          }
        } catch (error) {
          console.error('Error retrieving call data:', error);
        }
      }

      // ✅ IMPORTANT: Sync all current values to open ticket forms if sessionId exists
      if (sessionIdToUse) {
        syncFormValuesToOpenTicket();
      }

      // ✅ ALWAYS trigger the search API regardless of session state
      onSearch(filteredValues);

      // ✅ NEW: Use safe update function to preserve form data if we have sessionId
      try {
        if (sessionIdToUse) {
          safeUpdateSearchCriteria(sessionIdToUse, filteredValues);
        }
      } catch (error) {
        console.error('❌ Error updating search criteria in session:', error);
      }

      // ✅ ALWAYS set customer number for phone-based searches
      setCustomerNumber(form.getFieldValue('customerPhoneNumber') || '');
    } catch (error) {
      // Error handling
      console.error('Error in onFinish:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // ✅ ENHANCED: Better field blur handler to sync values
  const handleFieldBlur = (fieldName: string) => {
    const value = form.getFieldValue(fieldName);

    // ✅ FIX: Only sync values if currentSessionId exists
    if (value && currentSessionId) {
      syncFieldToMinimizedModals(fieldName, value);

      // Also notify open tickets about this field change
      notifyLookupFormUpdated(currentSessionId, { [fieldName]: value });
    }

    // Trigger search with all form values
    onFinish(form.getFieldsValue());
  };

  // CRITICAL FIX: Only initialize minimized modals when currentSessionId changes
  useEffect(() => {
    // ✅ CRITICAL FIX: Add a global rate limiter for this specific session
    const initKey = `lookup-form-init-${currentSessionId}`;
    const lastInitTime = parseInt(sessionStorage.getItem(initKey) || '0', 10);
    const now = Date.now();

    // Only allow initialization once every 2 seconds for the same session
    if (now - lastInitTime < 2000) {
      return;
    }

    // Record this initialization
    sessionStorage.setItem(initKey, now.toString());

    // ✅ MODIFIED: Add check to prevent initialization for placeholder-call session
    if (currentSessionId) {
      // Skip excessive logging for placeholder-call
      if (currentSessionId !== 'placeholder-call') {
      }

      // ✅ MODIFIED: Use a reference to track initialized sessions and prevent re-initialization
      const sessionIdToInitialize = currentSessionId;
      const isPlaceholderCall = currentSessionId === 'placeholder-call';

      // ✅ MODIFIED: For placeholder-call, use longer delay and only initialize once
      if (isPlaceholderCall) {
        // Use sessionStorage to track if we've already initialized for placeholder-call
        const alreadyInitialized = sessionStorage.getItem('placeholder-call-initialized');

        if (alreadyInitialized) {
          // Skip initialization if already done
          return;
        }

        // Mark as initialized
        sessionStorage.setItem('placeholder-call-initialized', 'true');

        setTimeout(() => {
          setTimeout(() => {
            sessionStorage.removeItem('placeholder-call-initialized');
          }, 5000); // ✅ REDUCED: From 10000ms to 5000ms

          if (sessionIdToInitialize === currentSessionId) {
            initializeMinimizedModals();
          }
        }, 500); // ✅ REDUCED: From 1000ms to 500ms
      } else if (typeof currentSessionId === 'number') {
        // Chat session
        // ✅ CRITICAL FIX: For chat sessions, check if already initialized recently
        const chatInitKey = `chat-modal-init-${currentSessionId}`;
        const lastChatInit = sessionStorage.getItem(chatInitKey);

        if (lastChatInit) {
          return;
        }

        // Mark chat session as initialized
        sessionStorage.setItem(chatInitKey, 'true');

        // Set a timeout to allow re-initialization after some time
        setTimeout(() => {
          sessionStorage.removeItem(chatInitKey);
        }, 2500); // ✅ REDUCED: From 5000ms to 2500ms

        setTimeout(() => {
          if (sessionIdToInitialize === currentSessionId) {
            initializeMinimizedModals();
          }
        }, 150); // ✅ REDUCED: From 300ms to 150ms
      } else {
        setTimeout(() => {
          if (sessionIdToInitialize === currentSessionId) {
            initializeMinimizedModals();
          }
        }, 150); // ✅ REDUCED: From 300ms to 150ms
      }
    }
  }, [currentSessionId, initializeMinimizedModals]);

  // ✅ CRITICAL FIX: Add new cleanup effect to remove session storage items
  useEffect(() => {
    return () => {
      if (currentSessionId) {
        sessionStorage.removeItem(`lookup-form-init-${currentSessionId}`);
        sessionStorage.removeItem(`chat-modal-init-${currentSessionId}`);
        sessionStorage.removeItem(`modal-init-${currentSessionId}`);
        sessionStorage.removeItem(`modal-sync-${currentSessionId}`);
      }
    };
  }, [currentSessionId]);

  // ✅ ADD: New effect to prevent minimized modals from disappearing
  useEffect(() => {
    // Add this effect to help preserve minimized modals during lookup->detail->lookup transitions
    if (minimizedModals.length > 0) {
      const logKey = `logged-preserving-${currentSessionId}`;
      if (!sessionStorage.getItem(logKey)) {
        sessionStorage.setItem(logKey, 'true');

        setTimeout(() => {
          sessionStorage.removeItem(logKey);
        }, 1500); // ✅ REDUCED: From 3000ms to 1500ms
      }
    }
  }, [minimizedModals, currentSessionId]);

  const [getServiceTypeDetail] = useLazyGetServiceTypeDetailQuery();

  // Other functionality like InitHandleMinimizeCreateTicketModal, handleMinimizeCreateTicketModal, etc.

  const InitHandleMinimizeCreateTicketModal = () => {
    try {
      // Get current session info
      const currentCallData = localStorage.getItem('currentCallDialingEvent');
      const chatSessionData = localStorage.getItem('omnichannel_chat_session');

      let sessionId: string | number | undefined;
      let sessionType: 'call' | 'chat';

      if (isCallSelected && currentCallData) {
        const callData = JSON.parse(currentCallData);
        sessionId = callData.parentCallUuid || callData.callUuid;
        sessionType = 'call';
      } else if (isChatSelected && chatSessionData) {
        const { chatSessionId } = JSON.parse(chatSessionData);
        sessionId = chatSessionId;
        sessionType = 'chat';
      } else {
        return;
      }

      const storedSessions = localStorage.getItem('customerSupportSession');
      if (!storedSessions) return;

      const sessions = JSON.parse(storedSessions);
      const currentSession = sessions.find((s: any) =>
        sessionType === 'call' ? s.callId === sessionId : s.chatSessionId === sessionId
      );

      if (!currentSession?.searchData) return;

      // Find the current tab if tabKey is provided, otherwise fall back to first UC tab
      const targetTab = tabKey
        ? currentSession.searchData.find((tab: any) => tab.key === tabKey)
        : currentSession.searchData.find((tab: any) => tab.key.startsWith('UC-'));

      if (!targetTab?.minimizedModals?.length) return;

      const restoredModals = targetTab.minimizedModals
        .map((modalId: number) => {
          const modalData = targetTab.formData[modalId];
          if (!modalData) return null;

          return {
            id: modalId,
            name: modalData.serviceTypeName || 'Unnamed Service',
            formData: modalData
          };
        })
        .filter(Boolean);

      if (restoredModals.length > 0) {
        setMinimizedModals(restoredModals);
      }
    } catch (error) {
      console.error('Error initializing minimized modals:', error);
    }
  };

  // Handle minimizing the create ticket modal
  const handleMinimizeCreateTicketModal = () => {
    if (selectedServiceTypeId !== null && formRef.current && serviceType?.data) {
      handleMinimizeModal(
        selectedServiceTypeId,
        formRef,
        serviceType.data.name,
        tabKey // Pass the current tab key
      );
      setIsCreateTicketModalOpen(false);
    }
  };

  // Create wrapper functions with the same signatures as the previous functions
  const deleteMinimizedModal = (id: number) => {
    // Call the hook function to update UI state and perform cleanup
    handleDeleteMinimizedModal(id, tabKey);
  };

  // Restore minimized modal with refreshed form data
  const restoreMinimizedModal = (id: number) => {
    const modalToRestore = handleRestoreModal(id, tabKey);

    if (modalToRestore) {
      setSelectedServiceTypeId(modalToRestore.id);
      setServiceTypeId(modalToRestore.id);
      setIsCreateTicketModalOpen(true);

      // ✅ FIX: Use timeout to wait for modal to render
      setTimeout(() => {
        // Get current lookup form values
        const lookupFormValues = form.getFieldsValue();

        // ✅ FIX: Read modalFormData directly from session storage
        let modalFormData: Record<string, any> = {};
        try {
          const storedSessions = localStorage.getItem('customerSupportSession');
          if (storedSessions && tabKey) {
            const sessions = JSON.parse(storedSessions);
            const currentSession = sessions.find((s: any) => s.searchData?.some((tab: any) => tab.key === tabKey));

            if (currentSession) {
              const currentTab = currentSession.searchData.find((tab: any) => tab.key === tabKey);
              if (currentTab?.formData?.[id]) {
                modalFormData = currentTab.formData[id];
              } else {
                modalFormData = modalToRestore.formData || {};
              }
            }
          } else {
            modalFormData = modalToRestore.formData || {};
          }
        } catch (error) {
          modalFormData = modalToRestore.formData || {};
        }

        // Process lookup values to match form field names
        const processedLookupValues: Record<string, any> = {
          customerRequestName: lookupFormValues.customerName,
          customerRequestPhoneNumber: lookupFormValues.customerPhoneNumber,
          customerRequestAddress: lookupFormValues.customerAddress,
          district: lookupFormValues.districtId,
          ward: lookupFormValues.wardId
        };

        const finalTicketData = {
          ...processedLookupValues,
          ...modalFormData
        };

        // Handle date conversion
        if (finalTicketData.appointmentDate && !dayjs.isDayjs(finalTicketData.appointmentDate)) {
          try {
            const parsedDate = dayjs(finalTicketData.appointmentDate);
            finalTicketData.appointmentDate = parsedDate.isValid() ? parsedDate : null;
          } catch (error) {
            finalTicketData.appointmentDate = null;
          }
        }

        // Set form data
        setTimeout(() => {
          if (formRef.current) {
            formRef.current.form.setFieldsValue(finalTicketData);
          }
        }, 50);
      }, 300);
    } else {
      console.error('❌ Modal not found for ID:', id);
    }
  };

  // Add a function to completely clear lookup session data
  const clearLookupSession = useCallback(() => {
    try {
      const storedSessions = localStorage.getItem('customerSupportSession');
      if (!storedSessions) return;

      const sessions = JSON.parse(storedSessions);

      // Find current session by chatSessionId or callId
      const currentCallData = localStorage.getItem('currentCallDialingEvent');
      const chatSessionData = localStorage.getItem('omnichannel_chat_session');

      let currentSessionId: string | number | undefined;
      let sessionType: 'call' | 'chat' | undefined;

      if (currentCallData) {
        const callData = JSON.parse(currentCallData);
        currentSessionId = callData.parentCallUuid || callData.callUuid;
        sessionType = 'call';
      } else if (chatSessionData) {
        const { chatSessionId } = JSON.parse(chatSessionData);
        currentSessionId = chatSessionId;
        sessionType = 'chat';
      }

      if (currentSessionId && sessionType) {
        const sessionIndex = sessions.findIndex((s: any) =>
          sessionType === 'call' ? s.callId === currentSessionId : s.chatSessionId === currentSessionId
        );

        if (sessionIndex >= 0) {
          // Get the current session data
          const currentSession = sessions[sessionIndex];

          // Create a clean search tab with empty data
          const emptySearchTab = {
            label: 'Kết quả tìm kiếm',
            key: `UC-${Date.now()}`,
            customerId: 0,
            customerCode: '',
            minimizedModals: [],
            formData: {},
            searchCriteria: {}
          };

          // Update session with clean data
          sessions[sessionIndex] = {
            ...currentSession,
            searchData: [emptySearchTab]
          };

          // Save back to localStorage
          localStorage.setItem('customerSupportSession', JSON.stringify(sessions));
        }
      }
    } catch (error) {
      console.error('Error clearing lookup session:', error);
    }
  }, []); // No dependencies needed as this doesn't rely on changing values

  // Add missing handleRefreshClick function
  const handleRefreshClick = () => {
    setIsRotating(true); // Animate the icon rotation

    // Reset form (or your existing logic)
    form.resetFields();
    setDisabledFields({});
    setCustomerNumber('');
    // Clear minimized modals
    setMinimizedModals([]);
    setCustomerCodeLength(0);
    setSearchBy('customerCode');
    setCustomerPhoneLabel(customerLookupMessages.customerPhone); // Ensure phone label is reset

    // Step 2: Fully reset the Ant Design form
    form.resetFields(); // Resets to initialValues defined on Form/Form.Item or the Form's initialValues prop.
    // Explicitly set all form fields to undefined to ensure they are cleared immediately,
    // regardless of what the <Form initialValues={initialValues}> prop might be at this exact moment.
    form.setFieldsValue({
      customerName: undefined,
      customerAddress: undefined,
      districtId: undefined,
      wardId: undefined,
      customerPhoneNumber: undefined,
      customerRequestCode: undefined,
      customerCode: undefined,
      meterSerialNumber: undefined,
      routeCode: undefined,
      meterPointCode: undefined
    });

    // Step 3: Notify parent to clear its criteria state
    onSearch({}); // This will trigger the parent to update initialValues,
    // and the useEffect in this component will run again, reinforcing the cleared state.

    // Step 4: Clear any session data related to lookup view
    clearLookupSession();

    setTimeout(() => {
      setIsRotating(false); // Stop icon rotation after 1 second
    }, 1000);
  };

  // Add missing handleCustomerCodeChange function
  const handleCustomerCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;

    // Nếu đang tìm kiếm theo customerCode, xóa tất cả khoảng trắng ngay lập tức
    if (searchBy === 'customerCode') {
      // Xóa khoảng trắng và cập nhật ngay
      inputValue = inputValue.replace(/\s+/g, '');
      e.target.value = inputValue;

      // Cập nhật form
      form.setFieldValue('customerCode', inputValue);
      setCustomerCodeLength(inputValue.length);
      return;
    }

    form.setFieldsValue({ [searchBy]: inputValue });
    setCustomerCodeLength(inputValue.length);
  };

  // Add missing handleCloseCreateTicketModal function
  const handleCloseCreateTicketModal = () => {
    setIsCreateTicketModalOpen(false);
  };

  // ✅ ENHANCED: Better session change detection with UI reset
  const prevSessionIdRef = useRef<string | number | undefined>(currentSessionId);

  useEffect(() => {
    if (prevSessionIdRef.current !== currentSessionId) {
      prevSessionIdRef.current = currentSessionId;

      let phoneNumberFromSession: string | undefined = undefined;
      let shouldResetFullFormAndPhoneState = true;

      // If an incoming call is being processed (isCallTriggered is true),
      // its handler is responsible for the phone number.
      // We should avoid fully resetting the form or the customerNumber state here.
      if (isCallTriggered) {
        shouldResetFullFormAndPhoneState = false;
      }

      if (shouldResetFullFormAndPhoneState) {
        // Reset form fields and basic states
        form.resetFields();
        setDisabledFields({});
        setCustomerCodeLength(0);
        setSearchBy('customerCode');

        // Check if we have search criteria for the new session
        if (currentSessionId) {
          try {
            const storedSessions = localStorage.getItem('customerSupportSession');
            if (storedSessions) {
              const sessions = JSON.parse(storedSessions);
              const sessionData = sessions.find(
                (s: any) =>
                  (typeof currentSessionId === 'string' && s.callId === currentSessionId) ||
                  (typeof currentSessionId === 'number' && s.chatSessionId === currentSessionId)
              );

              if (sessionData?.globalSearchCriteria) {
                const criteria = sessionData.globalSearchCriteria;
                form.setFieldsValue(criteria); // Apply all criteria to form

                // Update field enabled/disabled states
                const newDisabledFieldsState: Record<string, 'default' | 'enabled' | 'disabled'> = {};
                Object.keys(criteria).forEach((key) => {
                  if (criteria[key] !== undefined && criteria[key] !== null && criteria[key] !== '') {
                    newDisabledFieldsState[key] = 'enabled';
                  }
                });
                setDisabledFields(newDisabledFieldsState);

                // Specifically get phone number for customerNumber state
                if (criteria.customerPhoneNumber) {
                  phoneNumberFromSession = String(criteria.customerPhoneNumber);
                }
              }
            }
          } catch (error) {
            console.error('Error loading search criteria for new session:', error);
          }
        }

        // Set customerNumber state based on what was found or fallback to empty
        // This ensures customerNumber (used by useGetCustomerNameByPhoneNumber) is correctly updated
        setCustomerNumber(phoneNumberFromSession || '');
      } else {
        // If not doing a full reset (due to active call),
        // we still might want to reset non-phone related fields or apply other session criteria.
        // For now, the primary goal is to preserve the incoming phone number.
        // You could selectively reset other fields here if needed:
        // form.setFieldsValue({ customerName: undefined, customerAddress: undefined, ... });
        // setDisabledFields(prev => ({ ...prev, customerName: 'default', ...})); // etc.
      }

      // Also force reload the minimized modals
      if (currentSessionId && typeof initializeMinimizedModals === 'function') {
        // Adding a small delay to ensure other state updates related to session change might have settled
        setTimeout(() => {
          initializeMinimizedModals();
        }, 150);
      }
    }
  }, [currentSessionId, form, initializeMinimizedModals, isCallTriggered]); // Added isCallTriggered to dependencies

  // ✅ ADD: Effect to listen for minimizedModalsCleared event
  useEffect(() => {
    const handleMinimizedModalsCleared = (event: CustomEvent<{ sessionId: string | number }>) => {
      if (event.detail.sessionId === currentSessionId) {
        // Force clear minimized modals
        setMinimizedModals([]);

        // Clear any session storage items
        if (currentSessionId) {
          sessionStorage.removeItem(`lookup-form-init-${currentSessionId}`);
          sessionStorage.removeItem(`chat-modal-init-${currentSessionId}`);
          sessionStorage.removeItem(`modal-init-${currentSessionId}`);
          sessionStorage.removeItem(`modal-sync-${currentSessionId}`);
        }
      }
    };

    window.addEventListener('minimizedModalsCleared', handleMinimizedModalsCleared as EventListener);

    return () => {
      window.removeEventListener('minimizedModalsCleared', handleMinimizedModalsCleared as EventListener);
    };
  }, [currentSessionId, setMinimizedModals]);

  // ✅ NEW: Add effect to listen for form clear events
  useEffect(() => {
    const handleClearLookupForm = (event: CustomEvent) => {
      // Reset all form fields
      form.resetFields();

      // Reset component state
      setDisabledFields({});
      setCustomerNumber('');
      setCustomerCodeLength(0);
      setDisplayCustomerName(null);
      setCustomerPhoneLabel(customerLookupMessages.customerPhone);

      // Clear minimized modals
      if (typeof setMinimizedModals === 'function') {
        setMinimizedModals([]);
      }

      // Don't apply phone number yet if clearOnly is true
      if (event.detail?.clearOnly === true) {
      } else if (event.detail?.phoneNumber) {
        // Legacy behavior for backward compatibility

        setTimeout(() => {
          form.setFieldsValue({ customerPhoneNumber: event.detail.phoneNumber });
          setCustomerNumber(event.detail.phoneNumber);
          setDisabledFields((prev) => ({ ...prev, customerPhoneNumber: 'enabled' }));

          if (event.detail.forceSearch) {
            const searchCriteria = { customerPhoneNumber: event.detail.phoneNumber };
            onSearch(searchCriteria);
          }
        }, 200);
      }
    };

    window.addEventListener('clearLookupForm', handleClearLookupForm as EventListener);

    return () => {
      window.removeEventListener('clearLookupForm', handleClearLookupForm as EventListener);
    };
  }, [form, customerLookupMessages.customerPhone, setMinimizedModals, onSearch]);

  // Replace existing form clear handlers with completely separate events

  // Create a complete form reset handler (no phone setting)
  useEffect(() => {
    const handleFullFormReset = (event: CustomEvent) => {
      // Step 1: Reset the form completely
      form.resetFields();

      // Step 2: Ensure all form values are explicitly set to undefined
      // This is especially important for cases where initialValues might interfere
      form.setFieldsValue({
        customerName: undefined,
        customerAddress: undefined,
        districtId: undefined,
        wardId: undefined,
        customerPhoneNumber: undefined,
        customerRequestCode: undefined,
        customerCode: undefined,
        meterSerialNumber: undefined,
        routeCode: undefined,
        meterPointCode: undefined
      });

      // Step 3: Reset all component state
      setDisabledFields({});
      setCustomerNumber('');
      setCustomerCodeLength(0);
      setDisplayCustomerName(null);
      setCustomerPhoneLabel(customerLookupMessages.customerPhone);
      setSearchBy('customerCode');

      // Step 4: If force complete is set, make sure to clear search results by calling onSearch
      if (event.detail?.forceComplete) {
        onSearch({}); // Clear search results by passing empty criteria
      }

      // Step 5: Clear minimized modals if applicable
      if (typeof setMinimizedModals === 'function') {
        setMinimizedModals([]);
      }

      // Log to confirm reset is complete
    };

    window.addEventListener('formFullReset', handleFullFormReset as EventListener);

    return () => {
      window.removeEventListener('formFullReset', handleFullFormReset as EventListener);
    };
  }, [form, customerLookupMessages.customerPhone, setMinimizedModals, onSearch]);

  // Create a separate handler just for setting phone and triggering search
  useEffect(() => {
    const handleSetPhoneAndSearch = (event: CustomEvent) => {
      const phoneNumber = event.detail?.phoneNumber;

      if (phoneNumber) {
        // Step 1: Set phone in form
        form.setFieldsValue({ customerPhoneNumber: phoneNumber });

        // Step 2: Update customer number state to trigger name lookup
        setCustomerNumber(phoneNumber);

        // Step 3: Enable only the phone field
        setDisabledFields((prev) => ({
          ...prev,
          customerPhoneNumber: 'enabled'
        }));

        // Step 4: Create a clean search criteria object with ONLY the phone number
        const searchCriteria = { customerPhoneNumber: phoneNumber };

        // Step 5: Directly call onSearch with the clean criteria
        setTimeout(() => {
          onSearch(searchCriteria);
        }, 100); // Small delay to ensure form is updated
      }
    };

    window.addEventListener('setPhoneAndSearch', handleSetPhoneAndSearch as EventListener);

    return () => {
      window.removeEventListener('setPhoneAndSearch', handleSetPhoneAndSearch as EventListener);
    };
  }, [form, onSearch]);

  // Keep the existing event handlers for backward compatibility
  useEffect(() => {
    const handleClearLookupForm = (event: CustomEvent) => {
      if (event.detail?.clearOnly === true) {
        // If clearOnly flag is set, dispatch our new full reset event
        window.dispatchEvent(
          new CustomEvent('formFullReset', {
            detail: { timestamp: Date.now() }
          })
        );
      } else if (event.detail?.phoneNumber) {
        // For backward compatibility - handle both clear and phone setting
        form.resetFields();
        setDisabledFields({});
        setCustomerNumber('');

        setTimeout(() => {
          const phoneNumber = event.detail.phoneNumber;
          form.setFieldsValue({ customerPhoneNumber: phoneNumber });
          setCustomerNumber(phoneNumber);
          setDisabledFields((prev) => ({ ...prev, customerPhoneNumber: 'enabled' }));

          if (event.detail.forceSearch) {
            onSearch({ customerPhoneNumber: phoneNumber });
          }
        }, 200);
      } else {
        // Basic form clear
        form.resetFields();
        setDisabledFields({});
        setCustomerNumber('');
      }
    };

    window.addEventListener('clearLookupForm', handleClearLookupForm as EventListener);

    return () => {
      window.removeEventListener('clearLookupForm', handleClearLookupForm as EventListener);
    };
  }, [form, onSearch]);

  // Add new event handler for setPhoneAndSearch for compatibility
  useEffect(() => {
    const handleSetPhoneAndSearch = (event: CustomEvent) => {
      // Forward to our new event for consistency
      window.dispatchEvent(
        new CustomEvent('setPhoneAndTriggerSearch', {
          detail: event.detail
        })
      );
    };

    window.addEventListener('setPhoneAndSearch', handleSetPhoneAndSearch as EventListener);

    return () => {
      window.removeEventListener('setPhoneAndSearch', handleSetPhoneAndSearch as EventListener);
    };
  }, []);

  // ✅ RE-ENABLED: Automatic phone mapping for incoming calls
  useEffect(() => {
    const handleSetPhoneAndTriggerSearch = (event: CustomEvent) => {
      //console.log('🔍 CustomerLookupForm - Received setPhoneAndTriggerSearch event:', event.detail);
      const { phoneNumber, timestamp, isIncomingCall } = event.detail;

      // 🚨 CRITICAL: Only process if this is an incoming call to prevent overriding user input
      if (phoneNumber && isIncomingCall === true) {
        //console.log('✅ CustomerLookupForm - Processing incoming call phone mapping:', phoneNumber);
        // Set phone number in form
        form.setFieldsValue({ customerPhoneNumber: phoneNumber });
        setCustomerNumber(phoneNumber);
        setDisabledFields((prev) => ({ ...prev, customerPhoneNumber: 'enabled' }));

        //console.log('✅ CustomerLookupForm - Phone number set:', phoneNumber);

        // 🚀 OPTIMIZED: Check if form is ready immediately
        const currentFormValues = form.getFieldsValue();
        const isFormEmpty =
          !currentFormValues.customerName && !currentFormValues.customerAddress && !currentFormValues.customerCode;

        if (isFormEmpty) {
          // Form is empty/cleared, trigger search immediately
          //console.log('🚀 CustomerLookupForm - Form is ready, triggering immediate search');
          onSearch({ customerPhoneNumber: phoneNumber });
        } else {
          // Form might still be clearing, wait a bit
          //console.log('⏳ CustomerLookupForm - Form not ready, waiting before search');
          setTimeout(() => {
            //console.log('🔍 CustomerLookupForm - Triggering delayed search');
            onSearch({ customerPhoneNumber: phoneNumber });
          }, 50);
        }
      } else if (phoneNumber && isIncomingCall !== true) {
        //console.log('🚫 CustomerLookupForm - Skipping phone mapping (not incoming call):', phoneNumber, 'isIncomingCall:', isIncomingCall);
      }
    };

    window.addEventListener('setPhoneAndTriggerSearch', handleSetPhoneAndTriggerSearch as EventListener);

    return () => {
      window.removeEventListener('setPhoneAndTriggerSearch', handleSetPhoneAndTriggerSearch as EventListener);
    };
  }, [form, onSearch]);

  useEffect(() => {
    // Thiết lập mặc định ID quận là 90000
    setDistrictId(90000);
    form.setFieldsValue({ districtId: 90000 });
  }, [form]);

  const switchServiceTypeKeepValues = (nextId: number) => {
    const curr = formRef.current?.form?.getFieldsValue(true) || {};
    setSelectedServiceTypeId(nextId);
    setServiceTypeId(nextId);
    setTimeout(() => {
      formRef.current?.form?.setFieldsValue(curr);
    }, 0);
  };
  const [ticketInitialData, setTicketInitialData] = useState<any>(null);

  const openCreateTicketFirstTime = () => {
    form.resetFields(); // Reset form
    const rawLookup = form.getFieldsValue();
    const firstInit = {
      customerName: rawLookup.customerName,
      customerPhoneNumber: rawLookup.customerPhoneNumber,
      customerAddress: rawLookup.customerAddress,
      districtId: rawLookup.districtId,
      wardId: typeof rawLookup.wardId === 'object' ? rawLookup.wardId?.value : rawLookup.wardId
    };
    setTicketInitialData(firstInit);
    setCreateTicketModalKey((prev) => prev + 1); // Force form reset for new ticket
    setModalOpenTimestamp(Date.now()); // Track modal opening time
    setIsCreateTicketModalOpen(true);
  };

  return (
    <>
      <Button
        type='text'
        className='ml-[240px] mt-1 hover:bg-transparent focus:bg-transparent active:bg-transparent'
        icon={<RefreshIcon className={`${isRotating ? 'animate-spin' : ''}`} />}
        onClick={handleRefreshClick}
      >
        <Typography.Text className='text-[#1564E8]'>{customerLookupMessages.refresh}</Typography.Text>
      </Button>
      <Spin spinning={isRefreshing}>
        <Form
          form={form}
          onFinish={onFinish}
          layout='vertical'
          className='text-sm-label'
          initialValues={initialValues}
          // ✅ UPDATE: Enhanced onValuesChange to sync fields to minimized modals
          onValuesChange={(changedValues, allValues) => {
            // Sync each changed field to minimized modals
            Object.entries(changedValues).forEach(([fieldName, value]) => {
              syncFieldToMinimizedModals(fieldName, value);
            });
          }}
        >
          {/* Tên khách hàng */}
          <Form.Item name='customerName' label={renderLabel('customerName', customerLookupMessages.customerName)}>
            <Input
              disabled={disabledFields.customerName === 'disabled'}
              onChange={(e) => handleFieldChange('customerName', e.target.value)}
              onBlur={() => handleFieldBlur('customerName')}
            />
          </Form.Item>

          {/* Địa chỉ khách hàng */}
          <Form.Item
            name='customerAddress'
            label={renderLabel('customerAddress', customerLookupMessages.customerAddress)}
          >
            <Input
              disabled={disabledFields.customerAddress === 'disabled'}
              onChange={(e) => handleFieldChange('customerAddress', e.target.value)}
              onBlur={() => handleFieldBlur('customerAddress')}
            />
          </Form.Item>

          {/* Quận/Huyện - Phường/Xã (Chung label & disable chung) */}
          <Form.Item label={renderLabel('customerDistrictAndWard', `${customerLookupMessages.ward}`)}>
            <Row gutter={8}>
              <Col span={0} style={{ display: 'none' }}>
                <Form.Item name='districtId' noStyle initialValue={90000}>
                  <Input type='hidden' />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name='wardId' noStyle>
                  <Select
                    showSearch
                    optionFilterProp='children'
                    placeholder={
                      <Typography.Text className='text-[14px]/[20px] font-normal text-[#181818]'>
                        {customerLookupMessages.selectWard}
                      </Typography.Text>
                    }
                    loading={loadingWards}
                    options={wards?.map((itm) => ({
                      label: itm.administrativeUnitName,
                      value: itm.administrativeUnitId
                    }))}
                    filterOption={(input, option) =>
                      removeVietnameseTones(option?.label ?? '')
                        .toLowerCase()
                        .includes(removeVietnameseTones(input).toLowerCase())
                    }
                    dropdownStyle={{ minWidth: '500px' }}
                    onChange={(value) => {
                      // Only call handleFieldChange if the group is NOT disabled
                      if (disabledFields.customerDistrictAndWard !== 'disabled') {
                        handleFieldChange('wardId', value);
                      }
                    }}
                    allowClear
                    disabled={disabledFields.customerDistrictAndWard === 'disabled'}
                    onBlur={() => {
                      // Only call handleFieldBlur if the group is NOT disabled
                      if (disabledFields.customerDistrictAndWard !== 'disabled') {
                        handleFieldBlur('wardId');
                      }
                    }}
                    // Value directly from form to retain it visually even when disabled
                    value={form.getFieldValue('wardId')}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>

          {/* Số điện thoại */}
          <Form.Item
            name='customerPhoneNumber'
            label={renderLabel('customerPhoneNumber', customerLookupMessages.customerPhone)}
          >
            <Input
              disabled={disabledFields.customerPhoneNumber === 'disabled'}
              onChange={(e) => {
                // Xóa khoảng trắng và cập nhật ngay
                const cleanedValue = e.target.value.replace(/\s+/g, '');
                form.setFieldValue('customerPhoneNumber', cleanedValue);
                handleFieldChange('customerPhoneNumber', cleanedValue);
              }}
              onPaste={(e) => {
                e.preventDefault();
                // Xử lý paste: xóa khoảng trắng và cập nhật ngay
                const pastedText = e.clipboardData.getData('text').trim().replace(/\s+/g, '');
                form.setFieldValue('customerPhoneNumber', pastedText);
                handleFieldChange('customerPhoneNumber', pastedText);
              }}
              onBlur={() => handleFieldBlur('customerPhoneNumber')}
            />
          </Form.Item>

          {/* ✅ MODIFIED: Display name using displayCustomerName state */}
          {displayCustomerName && (
            <div className='animate-fadeIn mb-2 flex items-center gap-2 rounded-lg border border-green-500 bg-green-50 px-4 py-3 shadow-sm'>
              <div className='mr-2 h-2 w-2 flex-shrink-0 rounded-full bg-green-500'></div>
              <Typography.Text className='text-xs font-semibold text-green-600'>{displayCustomerName}</Typography.Text>
            </div>
          )}

          {/* Mã yêu cầu */}
          <Form.Item
            name='customerRequestCode'
            label={renderLabel('customerRequestCode', customerLookupMessages.requestCode)}
          >
            <Input
              disabled={disabledFields.customerRequestCode === 'disabled'}
              onChange={(e) => handleFieldChange('customerRequestCode', e.target.value)}
              onBlur={() => onFinish(form.getFieldsValue())}
            />
          </Form.Item>

          {/* Customer Code - Mã PE - Customer Code Type (Chung label & disable chung) */}
          <Form.Item name={searchBy} label={renderLabel(searchBy, customerLookupMessages.other)}>
            <Row gutter={16}>
              <Col span={12}>
                <Input
                  placeholder={searchBy === 'customerCode' ? customerLookupMessages.PE : undefined}
                  disabled={disabledFields[searchBy] === 'disabled'}
                  value={form.getFieldValue(searchBy)}
                  onChange={handleCustomerCodeChange}
                  onPaste={(e) => {
                    if (searchBy === 'customerCode') {
                      e.preventDefault();
                      // Xử lý paste: xóa khoảng trắng và cập nhật ngay
                      const pastedText = e.clipboardData.getData('text').trim().replace(/\s+/g, '');
                      const input = e.target as HTMLInputElement;
                      input.value = pastedText;
                      form.setFieldValue('customerCode', pastedText);
                      setCustomerCodeLength(pastedText.length);
                      // Trigger search
                      onFinish(form.getFieldsValue());
                    }
                  }}
                  onBlur={() => onFinish(form.getFieldsValue())}
                />
                {/*{searchBy === 'customerCode' && customerCodeLength > 0 && (*/}
                {/*  <Typography.Text*/}
                {/*    type={customerCodeLength < 13 ? 'warning' : customerCodeLength === 13 ? 'success' : 'danger'}*/}
                {/*    className='text-xs font-semibold'*/}
                {/*  >*/}
                {/*    {customerCodeLength < 13*/}
                {/*      ? `${customerLookupMessages.entered} ${customerCodeLength} ${customerLookupMessages.character} ${customerLookupMessages.enterMore} ${13 - customerCodeLength} ${customerLookupMessages.character}`*/}
                {/*      : customerCodeLength === 13*/}
                {/*        ? customerLookupMessages.maxLength13Success*/}
                {/*        : customerLookupMessages.maxLength13Warning}*/}
                {/*  </Typography.Text>*/}
                {/*)}*/}
              </Col>
              <Col span={12}>
                <Select
                  value={searchBy}
                  onChange={(value) => setSearchBy(value)}
                  options={[
                    { value: 'customerCode', label: customerLookupMessages.customerCode },
                    { value: 'meterSerialNumber', label: customerLookupMessages.meterSerialNumber },
                    { value: 'routeCode', label: customerLookupMessages.routeCode },
                    { value: 'meterPointCode', label: customerLookupMessages.meterPointCode },
                    { value: 'customerCicNumber', label: customerLookupMessages.customerCicNumber }
                  ]}
                  disabled={disabledFields[searchBy] === 'disabled'}
                  onBlur={() => onFinish(form.getFieldsValue())}
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <div className={'mx-1.5 mt-1.5 w-full space-y-2.5'}>
                {searchBy === 'customerCode' && customerCodeLength > 0 && (
                  <Typography.Text
                    type={customerCodeLength < 13 ? 'warning' : customerCodeLength === 13 ? 'success' : 'danger'}
                    className='mt-1.5 text-xs font-semibold'
                  >
                    {customerCodeLength < 13
                      ? `${customerLookupMessages.entered} ${customerCodeLength} ${customerLookupMessages.character} ${customerLookupMessages.enterMore} ${13 - customerCodeLength} ${customerLookupMessages.character}`
                      : customerCodeLength === 13
                        ? customerLookupMessages.maxLength13Success
                        : customerLookupMessages.maxLength13Warning}
                  </Typography.Text>
                )}

                <div>
                  {searchBy === 'customerCode' && customerCodeLength === 13 && (
                    <VipCustomerLookup customerCode={form.getFieldValue('customerCode')} />
                  )}
                </div>
              </div>
            </Row>
          </Form.Item>

          {/* Nút tìm kiếm */}
          <Form.Item>
            <Button
              htmlType='submit'
              icon={isSearching ? <Spin size='small' /> : <SearchIcon />}
              className='mt-2 w-[360px] border-[#1564E8] bg-[#1564E8] font-semibold text-[#FFFFFF]'
              loading={isSearching}
              disabled={isSearching}
            >
              {isSearching ? 'Đang tìm kiếm...' : customerLookupMessages.lookup}
            </Button>
          </Form.Item>

          {/* Nút đăng ký mới và Tạo phiếu không định danh */}
          <Form.Item>
            <Button
              icon={<PlusIcon />}
              className='w-[360px] border-[#06A77D] bg-[#06A77D] font-semibold text-[#FFFFFF]'
              onClick={() => setShowQuickSelect(!showQuickSelect)}
            >
              {customerLookupMessages.newRegister}
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              className='w-[360px] border-[#FF9500] bg-[#FF9500] font-semibold text-[#FFFFFF]'
              onClick={() => {
                setSearchKeyword('');
                setIsServiceTypeModalOpen(true);
              }}
            >
              {customerLookupMessages.createAnonymousTicket}
            </Button>
          </Form.Item>
        </Form>
      </Spin>
      <Modal
        open={showQuickSelect}
        onCancel={() => setShowQuickSelect(false)}
        footer={null}
        centered
        width={400}
        title={customerLookupMessages.newRegister}
      >
        <div className='mt-2'>
          <div className='flex flex-col gap-2'>
            {[
              { label: customerLookupMessages.lowVoltagePowerSupply, parentId: 128 },
              { label: customerLookupMessages.mediumVoltagePowerSupply, parentId: 129 },
              { label: customerLookupMessages.highVoltagePowerSupply, parentId: 130 }
            ].map((btn, index) => (
              <Button
                key={index}
                className='w-[360px] border-[#FF9500] bg-[#FF9500] font-semibold text-[#FFFFFF]'
                onClick={() => {
                  setSelectedParentId(btn.parentId);
                  setIsServiceTypeOptionsModalOpen(true);
                  setShowQuickSelect(false); // Đóng modal sau khi chọn
                }}
              >
                {btn.label}
              </Button>
            ))}
          </div>
        </div>
      </Modal>
      <Modal
        className='ant-modal-body-p-none shadow-[0px 6px 24px #00000029] w-[1068px] overflow-y-scroll rounded-xl'
        open={isServiceTypeModalOpen}
        onCancel={() => setIsServiceTypeModalOpen(false)}
        footer={null}
        zIndex={1100}
        centered
      >
        <div className='mb-4 flex flex-col rounded-xl bg-[#F5F5F7]'>
          <Typography.Text className='my-4 ml-6 text-left text-[26px]/[34px] font-bold tracking-normal text-[#141414] opacity-100'>
            {customerLookupMessages.selectServiceType}
          </Typography.Text>
          {/*
            // onSelectServiceType={(id) => {
            //   if (!isCreateTicketModalOpen) setIsCreateTicketModalOpen(true); // mở nếu chưa mở
            //   switchServiceTypeKeepValues(id);
            // }}
            // initialKeyword={searchKeyword}
          */}
          <ServiceTypeList
            // onSelectServiceType={(serviceTypeId) => {
            //   setSelectedServiceTypeId(serviceTypeId);
            //   setServiceTypeId(serviceTypeId);
            //   setIsServiceTypeModalOpen(false);
            //   // setCreateTicketModalKey(prev => prev + 1); // Increment key to force remount for new ticket
            //   setIsCreateTicketModalOpen(true); // Mở modal tạo phiếu
            // }}

            onSelectServiceType={(id) => {
              setIsServiceTypeModalOpen(false);
              if (!isCreateTicketModalOpen) {
                // 🚨 CRITICAL FIX: When opening modal for first time, reset form
                setSelectedServiceTypeId(id); // Set selected service type ID for minimize function
                setServiceTypeId(id); // Set service type directly
                setCreateTicketModalKey((prev) => prev + 1); // Force form reset
                setModalOpenTimestamp(Date.now()); // Track modal opening time
                setIsCreateTicketModalOpen(true); // Open modal
              } else {
                // Modal already open, just switch service type and keep values
                switchServiceTypeKeepValues(id);
              }
            }}
            initialKeyword={searchKeyword}
          />
        </div>
      </Modal>
      <Modal
        className='ant-modal-body-p-none w-[1068px] overflow-y-scroll rounded-xl'
        open={isServiceTypeOptionsModalOpen}
        onCancel={() => setIsServiceTypeOptionsModalOpen(false)} // Đóng modal
        footer={null}
        zIndex={1100}
        centered
      >
        <div className='mb-4 flex flex-col rounded-xl bg-[#F5F5F7]'>
          <Typography.Text className='my-4 ml-6 text-left text-[26px]/[34px] font-bold tracking-normal text-[#141414] opacity-100'>
            {customerLookupMessages.selectServiceType}
          </Typography.Text>
          <ServiceTypeListOptions
            parentId={selectedParentId ?? 0} // Truyền parentId vào ServiceTypeListOptions
            onSelectServiceType={(serviceTypeId) => {
              setSelectedServiceTypeId(serviceTypeId); // Lưu serviceTypeId đã chọn
              setServiceTypeId(serviceTypeId);
              setIsServiceTypeOptionsModalOpen(false); // Đóng modal
              setCreateTicketModalKey((prev) => prev + 1); // Increment key to force remount for new ticket
              setModalOpenTimestamp(Date.now()); // Track modal opening time
              setIsCreateTicketModalOpen(true); // Mở modal tạo phiếu
            }}
          />
        </div>
      </Modal>
      <Modal
        className='ant-modal-body-p-none w-[1536px]'
        centered
        closable={false}
        onCancel={handleMinimizeCreateTicketModal}
        open={isCreateTicketModalOpen}
        footer={null}
      >
        <div className='sticky top-0 z-10 flex h-[64px] flex-row items-center justify-between rounded-t-xl border-b bg-white'>
          <div
            className='ml-6 flex cursor-pointer flex-row items-center gap-2'
            onClick={() => setIsServiceTypeModalOpen(true)}
          >
            <Typography.Text className='text-left text-[24px]/[32px] font-semibold tracking-normal text-[#141414] opacity-100'>
              {serviceType?.data?.name}
            </Typography.Text>
            <Button
              shape='circle'
              onClick={() => setIsServiceTypeModalOpen(true)}
              className='border-[#F5F5F7] bg-[#F5F5F7] bg-transparent bg-clip-padding bg-no-repeat opacity-100 hover:bg-[#EBEBED]'
              icon={<ArrowDownIcon className='h-4 w-4' />}
            />
          </div>
          <div className='my-4 mr-4 flex flex-row gap-6'>
            <Popconfirm
              title={customerLookupDetailedMessages.comfirmDeteleTicket}
              onConfirm={handleCloseCreateTicketModal}
              okText={customerLookupDetailedMessages.confirmYes}
              cancelText={customerLookupDetailedMessages.confirmCancel}
            >
              <Button
                shape='circle'
                className='border-[#F5F5F7] bg-[#F5F5F7] bg-transparent bg-clip-padding bg-no-repeat opacity-100 hover:bg-[#EBEBED]'
                icon={<TrashBinIcon className='h-4 w-4 text-colorError' />}
              />
            </Popconfirm>
            <Button
              shape='circle'
              onClick={handleMinimizeCreateTicketModal}
              className='border-[#F5F5F7] bg-[#F5F5F7] bg-transparent bg-clip-padding bg-no-repeat opacity-100 hover:bg-[#EBEBED]'
              icon={<DashIcon className='h-4 w-4' />}
            />
          </div>
        </div>
        <div className='flex flex-row'>
          <CreateTicketForm
            key={`create-ticket-form-${serviceTypeId}-${createTicketModalKey}-${modalOpenTimestamp}`} // Include timestamp to ensure fresh form
            ref={formRef}
            customerId={0}
            serviceTypeId={serviceTypeId}
            onCloseModal={handleCloseCreateTicketModal}
            currentSessionId={currentSessionId} // ✅ ADD: Pass currentSessionId
            initialFormData={(() => {
              // Lấy tất cả giá trị form hiện tại
              const rawValues = form.getFieldsValue();

              // Biến đổi các giá trị phức tạp thành đơn giản
              const transformedValues = {
                customerName: rawValues.customerName,
                customerPhoneNumber: rawValues.customerPhoneNumber,
                customerAddress: rawValues.customerAddress,
                districtId: rawValues.districtId,
                wardId:
                  rawValues.wardId && typeof rawValues.wardId === 'object' ? rawValues.wardId.value : rawValues.wardId

                // Trường hợp các giá trị khác cần được biến đổi có thể thêm vào đây
              };

              // Remove undefined values
              Object.keys(transformedValues).forEach((key) => {
                if (transformedValues[key as keyof typeof transformedValues] === undefined) {
                  delete (transformedValues as Record<string, any>)[key];
                }
              });

              return transformedValues;
            })()}
          />
          <CreateTicketInStructions customerId={0} serviceTypeId={serviceTypeId as number} />
        </div>
      </Modal>
      {/* ✅ MODIFIED: Only show minimized modals when displayGlobalModals is true */}
      {(() => {
        return displayGlobalModals && !isInDetailView;
      })() && (
          <div className='fixed bottom-0 right-0 z-[1] flex w-fit gap-2 p-2'>
            {minimizedModals.map((modal, index) => (
              <div
                className='animate-slideInUp flex h-10 w-[328px] transform cursor-pointer items-center justify-between rounded-t-lg bg-[#1564E8] transition-all duration-300 ease-in-out hover:scale-105 hover:shadow-lg'
                key={modal.id}
                onClick={() => restoreMinimizedModal(modal.id)}
                style={{
                  animationDelay: `${index * 100}ms`
                }}
              >
                <div className='flex min-w-0 flex-1 items-center px-3'>
                  {/* ✨ ADD: Service type icon */}
                  <div className='mr-2 h-2 w-2 flex-shrink-0 rounded-full bg-white'></div>

                  <Typography.Text className='truncate text-left text-[16px]/[20px] font-normal tracking-normal text-[#FFFFFF] opacity-100'>
                    {modal.name}
                  </Typography.Text>

                  {/* ✨ ADD: Progress indicator if form has data */}
                  {modal.formData && Object.keys(modal.formData).length > 0 && (
                    <div className='ml-2 h-1.5 w-1.5 flex-shrink-0 animate-pulse rounded-full bg-green-400'></div>
                  )}
                </div>

                <div className='mr-2 flex gap-1'>
                  <Popconfirm
                    title={customerLookupDetailedMessages.comfirmDeteleTicket}
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      deleteMinimizedModal(modal.id);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                    okText={customerLookupDetailedMessages.confirmYes}
                    cancelText={customerLookupDetailedMessages.confirmCancel}
                  >
                    <Button
                      shape='circle'
                      size='small'
                      onClick={(e) => e.stopPropagation()}
                      className='border-transparent bg-transparent text-white transition-colors duration-200 hover:bg-[#FFFFFF29] hover:text-red-300'
                      icon={<TrashBinWhiteIcon className='h-3 w-3' />}
                    />
                  </Popconfirm>
                  <Button
                    shape='circle'
                    size='small'
                    onClick={(e) => {
                      e.stopPropagation();
                      restoreMinimizedModal(modal.id);
                    }}
                    className='border-transparent bg-transparent text-white transition-colors duration-200 hover:bg-[#FFFFFF29] hover:text-green-300'
                    icon={<ExpandMinimizeModalIcon className='h-3 w-3' />}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
    </>
  );
};

export default CustomerLookupForm;
